import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  Dialog,
  Disclose,
  ErrorState,
  Headline,
  Loading,
  Refusal,
  Tooltip,
} from "@lopie/kernel-ui";

/**
 * **This is no longer a screen.** It is where the access half of
 * `People.jsx` lives: the group chips, the preview dialog and the
 * sentences about what a grant would expose. The screen that renders
 * them is `People`, which joins this half to the notification roster on
 * the email address, because both were listing the same people.
 *
 * Who can see what, and changing it (LO-58).
 *
 * Until this screen, adding a tag to a user was `aws cognito-idp
 * admin-add-user-to-group` typed into a terminal — which meant the one change
 * nobody could review afterwards was also the one nobody had to.
 *
 * The argument the screen has to make, and the reason it is not a table of
 * checkboxes:
 *
 * **The consequence is shown before the change.** Adding `customer` says
 * "this makes 3 more Organizations and 1 more Person visible to them", from
 * the kernel, before anything happens. A checkbox lets somebody find out
 * afterwards, and afterwards is not a thing you can undo for a person who has
 * already looked.
 *
 * **The counterintuitive rule is stated where somebody is about to act on
 * it.** Tags restrict. An untagged record is visible to everyone, so removing
 * every tag from a person does not hide everything from them — it leaves them
 * every untagged record. That sentence belongs next to the button, not in a
 * document.
 *
 * **Everything is rendered from the kernel.** The tag vocabulary, which
 * groups are tags, which of them any manifest declares, whether this app may
 * preview or grant at all, and every number. A tag added to `schema.TAGS`
 * appears here with no change to this file.
 *
 * **The numbers are re-read afterwards, never assumed.** Both directory
 * writes are idempotent and neither reports whether it changed anything, so
 * "granted" comes from reading the pool back rather than from believing the
 * request worked.
 *
 * **Whether *you* may grant is answered before you click (LO-82).** Granting
 * needs two things: the app declares `access.grant` and you are on this
 * kernel's committed governor list. Both come from `GET /access`, so the
 * chips are inert with a sentence saying which half is missing rather than a
 * disabled control that fails on the way back. A disabled button with no
 * explanation is worse than the honest warning this replaces — and the
 * warning it replaces ("this checked the app may grant, not that you may")
 * became false the moment LO-82 landed, so it is gone rather than kept.
 */

export function Label({ children }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
      {children}
    </Typography>
  );
}

/**
 * A chip that grows taller rather than wider.
 *
 * A MUI `Chip` label is `white-space: nowrap` and the root has no max-width,
 * so a chip whose text has no break opportunity pushes the page sideways and
 * nothing about the component says it will. A Cognito group name is exactly
 * that text: it is arbitrary, it is often an email-shaped or hyphenated
 * string, and one of 53 characters took the body to 495px at a 390px viewport
 * — found by measuring `scrollWidth` against `clientWidth`, which is the only
 * way any of these have ever been found.
 *
 * Wrapping rather than ellipsis, deliberately. A truncated group name is a
 * permission a reader cannot check, and this screen's whole argument is that
 * you can see exactly what somebody holds. `height: auto` is required with it:
 * without it the chip keeps its fixed height and the second line escapes the
 * border instead of the page.
 */
const WRAPPING_CHIP = {
  maxWidth: "100%",
  height: "auto",
  minHeight: 24,
  "& .MuiChip-label": {
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    py: 0.4,
  },
};

/**
 * A group, coloured by whether it does anything.
 *
 * Colour is never the only signal — every state carries a word in the
 * tooltip and a symbol in the label, because a permission rendered as a green
 * dot is a permission a colour-blind operator cannot read.
 */
export function GroupChip({ group, onClick, held, disabled }) {
  const inert = !group.grants;
  const colour = inert ? "kernel.stale" : "kernel.permitted";
  const name = inert ? `${group.name} · grants nothing` : group.name;
  return (
    <Tooltip title={group.detail}>
      <span>
        <Chip
          size="small"
          // The tick, not only the weight and the fill. Held and not-held
          // differing by border thickness alone is a state that does not read
          // at a glance and does not read at all in a screenshot of a
          // greyscale printout.
          label={held ? `✓ ${name}` : name}
          aria-label={`${group.name} — ${held ? "held" : "not held"}`}
          variant="outlined"
          onClick={onClick}
          disabled={disabled}
          clickable={Boolean(onClick)}
          sx={{
            ...WRAPPING_CHIP,
            bgcolor: held ? "action.selected" : "transparent",
            borderColor: colour,
            color: colour,
            borderWidth: held ? 2 : 1,
            borderStyle: "solid",
            fontWeight: held ? 600 : 400,
          }}
        />
      </span>
    </Tooltip>
  );
}

/**
 * "3 more organizations", and never "+3" on its own.
 *
 * The plural comes from the kernel's `collection`, not from appending an `s`.
 * The first type whose plural is not its name plus an `s` is Person, which is
 * also the type this feature is most about — "4 more Persons" would have been
 * the visible half of a UI keeping its own copy of the kernel's metadata.
 */
export function deltaSentence(types, subject) {
  const moved = (types || []).filter((t) => t.permitted && t.delta !== 0);
  if (!moved.length) return null;
  const parts = moved.map((t) => {
    const n = Math.abs(t.delta);
    const word = n === 1 ? t.type.toLowerCase() : t.collection || `${t.type}s`;
    return `${n} ${t.delta > 0 ? "more" : "fewer"} ${word}`;
  });
  const joined =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  return `This makes ${joined} visible to ${subject}.`;
}

/**
 * What a change would do, before it is made.
 *
 * Three things this refuses to flatten:
 *
 * - a type the app never declared shows a reason, not a zero. Zero is a claim
 *   about the data; "not declared" is a claim about the manifest.
 * - a capped count is shown as a floor. "at least 5000" and "5000" are
 *   different facts and rendering them the same would be worse than showing
 *   neither.
 * - a change that moves nothing says so out loud, because "nothing happens"
 *   is exactly the answer somebody about to click needs most.
 */
export function PreviewDialog({ pending, preview, failure, busy, onCancel, onConfirm,
                         canPreview, granting }) {
  const open = Boolean(pending);
  const verb = pending?.op === "add" ? "Add" : "Remove";
  const sentence = preview
    ? deltaSentence(preview.types, preview.subject)
    : null;
  const capped = (preview?.types || []).some(
    (t) => t.permitted && !t.delta_exact,
  );

  return (
    <Dialog
      open={open}
      onClose={busy || granting ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="access-preview-title"
    >
      <DialogTitle id="access-preview-title">
        {verb} <code>{pending?.tag}</code>{" "}
        {pending?.op === "add" ? "for" : "from"} {pending?.subject}
      </DialogTitle>

      <DialogContent dividers>
        {!canPreview ? (
          // The app may grant and may not count. Saying so is the only honest
          // option: a confirm dialog with no numbers that did not explain
          // their absence would read as "no change".
          <Alert severity="warning" variant="outlined">
            <AlertTitle>No preview available</AlertTitle>
            This app does not declare <code>access.preview</code>, so the
            kernel will not count what this would expose. You can still make
            the change — you will find out what it did afterwards, which is
            the thing this screen exists to avoid.
          </Alert>
        ) : busy ? (
          <Loading label="Counting what this would expose" minHeight={120} />
        ) : failure ? (
          <ErrorState
            failure={failure}
            title="Could not work out what this would do"
          />
        ) : preview ? (
          <Stack spacing={2}>
            {sentence ? (
              <Alert
                severity={pending.op === "add" ? "info" : "success"}
                variant="outlined"
                icon={false}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {sentence}
                </Typography>
              </Alert>
            ) : (
              <Alert severity="success" variant="outlined" icon={false}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Nothing new becomes visible.
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Every count below is the same on both sides. Either no record
                  carries this tag today, or this app never declared it on a
                  type that does.
                </Typography>
              </Alert>
            )}

            {capped ? (
              <Alert severity="warning" variant="outlined">
                <AlertTitle>One of these counts hit its cap</AlertTitle>
                A capped count is a floor, not a total, so the difference shown
                is the difference between two floors. The real change may be
                larger.
              </Alert>
            ) : null}

            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Visible now</TableCell>
                    <TableCell align="right">After</TableCell>
                    <TableCell align="right">Change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.types.map((t) => (
                    <TableRow key={t.type}>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {t.type}
                      </TableCell>
                      {t.permitted ? (
                        <>
                          <TableCell align="right">
                            <Tooltip title={t.visible_before.detail}>
                              <span>
                                {t.visible_before.exact ? "" : "≥ "}
                                {t.visible_before.counted}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={t.visible_after.detail}>
                              <span>
                                {t.visible_after.exact ? "" : "≥ "}
                                {t.visible_after.counted}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: t.delta === 0 ? 400 : 700,
                              // Amber marks *widening*, and nothing marks
                              // narrowing. Colouring a revoke green would say
                              // that taking access away is the good outcome,
                              // which is a judgement this screen has no
                              // business making — the sign already carries
                              // the direction, and it carries it without
                              // colour.
                              color:
                                t.delta > 0
                                  ? "kernel.attention"
                                  : t.delta < 0
                                    ? "text.primary"
                                    : "text.secondary",
                            }}
                          >
                            {t.delta > 0 ? `+${t.delta}` : t.delta}
                            {t.delta_exact ? null : (
                              <Tooltip title={t.detail}>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 0.5 }}
                                >
                                  capped
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                        </>
                      ) : (
                        <TableCell colSpan={3} sx={{ color: "text.secondary" }}>
                          {/* Not a zero. Zero would be a claim about the
                              data; this is a claim about the manifest. */}
                          not declared by this app — nothing of this type is
                          counted either side
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Box>
              <Label>What these numbers do not prove</Label>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
                {preview.does_not_prove.map((line) => (
                  <Typography
                    component="li"
                    variant="body2"
                    color="text.secondary"
                    key={line}
                  >
                    {line}
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        {/* `autoFocus` on Cancel, and it is the accessibility fix rather than
            a preference about which button is default.

            Opening this dialog puts `aria-hidden` on the page behind it, and
            the chip that opened it kept the focus — so a screen reader user
            was focused on an element the browser had just hidden from them,
            which Chrome reports and which leaves them with nothing to read.
            Focus has to land inside the dialog.

            Cancel rather than the confirm button because this dialog exists
            to be read before it is answered. Landing on the destructive-ish
            half and letting Enter apply a grant would undo the reason there
            is a preview at all. */}
        <Button onClick={onCancel} disabled={granting} autoFocus>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={busy || granting}
          color={pending?.op === "add" ? "primary" : "inherit"}
        >
          {granting ? "Applying…" : `${verb} ${pending?.tag || ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * One person in the sign-in pool: who they are, and every group they hold.
 *
 * `footer` is what `People.jsx` renders underneath — the other half of the
 * same person, which is what they are *told* about. It is a slot rather than
 * a second card because the two halves describe one human being, and two
 * bordered boxes side by side said they were two.
 *
 * Everything above the slot is unchanged and none of it is optional. The
 * groups this app cannot change are drawn disabled rather than omitted,
 * because a membership missing from the screen looks like a membership that
 * does not exist; `grants_nothing` is spelled out; and holding no group at
 * all gets its own sentence, because "in no groups" is not "sees nothing"
 * when tags restrict.
 */
export function PrincipalCard({ principal, groups, grantable, mayGrant,
                                onChange, busy, footer }) {
  const byName = Object.fromEntries(groups.map((g) => [g.name, g]));
  const held = new Set(principal.groups);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}
      >
        <Typography
          variant="h2"
          sx={{ fontFamily: "monospace", overflowWrap: "anywhere",
                minWidth: 0 }}
        >
          {principal.username}
        </Typography>
        {principal.is_self ? (
          <Tooltip title="You. A governor can change their own access — nothing stops that, and the audit log naming who did it is what makes it reviewable rather than invisible.">
            <Chip size="small" variant="outlined" label="you" />
          </Tooltip>
        ) : null}
        {principal.is_governor ? (
          // Word and symbol, not colour alone. Being a governor is authority
          // over permissions and grants no tag, so it must not read as a
          // wider set of records — hence its own chip rather than a chip in
          // the row of groups.
          <Tooltip title="A governor of this kernel: may grant and revoke tags, and approve or reject pending apps. Named on a committed list applied out of band — no button here adds one. It grants no tag and widens no read.">
            <Chip
              size="small"
              variant="outlined"
              label="⚖ governor"
              sx={{ color: "kernel.attention",
                    borderColor: "kernel.attention" }}
            />
          </Tooltip>
        ) : null}
        {!principal.enabled ? (
          <Chip
            size="small"
            variant="outlined"
            label="disabled"
            sx={{ color: "kernel.refused", borderColor: "kernel.refused" }}
          />
        ) : null}
        {principal.status && principal.status !== "CONFIRMED" ? (
          <Chip size="small" variant="outlined" label={principal.status} />
        ) : null}
      </Stack>

      <Label>
        {mayGrant ? "Groups held — click to change" : "Groups held"}
      </Label>
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
        {grantable.map((tag) => {
          const group = byName[tag] || { name: tag, grants: true, detail: "" };
          const has = held.has(tag);
          return (
            <GroupChip
              key={tag}
              group={group}
              held={has}
              disabled={!mayGrant || busy}
              onClick={
                mayGrant
                  ? () =>
                      onChange({
                        subject: principal.username,
                        tag,
                        op: has ? "remove" : "add",
                      })
                  : undefined
              }
            />
          );
        })}
        {/* Groups this person holds that this app cannot change: either not a
            tag at all, or a tag this app never declared. Shown rather than
            hidden — a membership missing from the screen looks like a
            membership that does not exist. */}
        {principal.groups
          .filter((g) => !grantable.includes(g))
          .map((g) => (
            <GroupChip
              key={g}
              group={
                byName[g] || {
                  name: g,
                  grants: false,
                  detail: "Not a group this kernel knows about.",
                }
              }
              held
              disabled
            />
          ))}
      </Stack>

      {principal.grants_nothing?.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Holds {principal.grants_nothing.join(", ")}, which no app has asked
          for — so it widens nothing for any app, whatever records exist. That
          is provable rather than observed: every read is limited to what the
          app asked for, and nothing asks for this.
        </Typography>
      ) : null}

      {!principal.groups.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          In no groups — which is not "sees nothing". Tags restrict, so this
          person sees every untagged record and no tagged one.
        </Typography>
      ) : null}
      {footer}
    </Paper>
  );
}

